import { ChevronDown, Store } from "lucide-react";
import type { Reseller as ApiReseller, SalesStatistic as ApiSalesStatistic } from "randmar-api-client";
import {
  useGetV4ManufacturerByRouteManufacturerIdResellerAndResellerIdQuery,
} from "randmar-api-client";
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
  RmBodyZone,
  SalesChart,
  Skeleton,
} from "randmarcomps";
import * as React from "react";
import { Link, useParams } from "react-router-dom";
import OpenOrdersGrid from "../components/grids/OpenOrdersGrid";
import OpportunitiesGrid from "../components/grids/OpportunitiesGrid";
import ReturnGrid from "../components/grids/ReturnGrid";
import DataRefreshAssistantCluster from "../components/layout/DataRefreshAssistantCluster";
import RmPageHeader from "../components/layout/RmPageHeader";
import ResellerActions from "../components/reseller/ResellerActions";
import SalesSummary from "../componentsCore/cards/SalesSummary";
import OpenOrdersSummary from "../componentsCore/cards/OpenOrdersSummary";
import { useManufacturer } from "../contexts/WithManufacturer";
import Path from "../routes/Path";
import Reseller from "../stores/reseller/Reseller";
import { formatRandmarDate } from "../utils/dates";
import withRoot from "../withRoot";

const sectionIds = {
  activeOrders: "reseller-active-orders",
  completedOrders: "reseller-completed-orders",
  returns: "reseller-returns",
  opportunities: "reseller-opportunities",
  qualifications: "reseller-qualifications",
};

const formatResellerDate = (value?: number | string | null, fallback = "No activity yet") =>
  formatRandmarDate(value) ?? fallback;

function buildResellerAssistantPrompt(options: {
  applicationId: string;
  manufacturerName: string;
  resellerId: string;
  resellerName: string;
  isQualified: boolean;
  onHold: boolean;
  qualificationId?: string;
  lastInvoiceDateLabel: string;
  joinedDateLabel: string;
  activityCounts: {
    activeOrders: number;
    completedOrders: number;
    returns: number;
    opportunities: number;
  };
  salesSummary: {
    salesLast30Days: number;
    salesLast3Months: number;
    salesYearToDay: number;
  };
}) {
  const lines: string[] = [
    "You are assisting with the Randmar Manufacturer Portal Reseller page.",
    `Manufacturer applicationId: ${options.applicationId}`,
    `Manufacturer name: ${options.manufacturerName}`,
    "",
    `Reseller: ${options.resellerName} (${options.resellerId})`,
    `Status: ${options.onHold ? "On hold" : "Active"}`,
    `Qualified for this manufacturer: ${options.isQualified ? "Yes" : "No"}`,
    options.qualificationId ? `Qualification ID: ${options.qualificationId}` : "Qualification ID: None",
    "",
    `Last invoice: ${options.lastInvoiceDateLabel}`,
    `Joined Randmar: ${options.joinedDateLabel}`,
    "",
    "Activity counts:",
    `- Active orders: ${options.activityCounts.activeOrders}`,
    `- Completed orders: ${options.activityCounts.completedOrders}`,
    `- Returns: ${options.activityCounts.returns}`,
    `- Opportunities: ${options.activityCounts.opportunities}`,
    "",
    "Sales summary:",
    `- Last 30 days: ${options.salesSummary.salesLast30Days}`,
    `- Last 3 months: ${options.salesSummary.salesLast3Months}`,
    `- Year to day: ${options.salesSummary.salesYearToDay}`,
    "",
    "Please answer questions and suggest next actions based only on the page context above.",
  ];

  return lines.join("\n");
}

const ResellerPage: React.FC = () => {
  const { applicationId } = useManufacturer();
  const { resellerId } = useParams<{ resellerId: string }>();
  const [lastRefreshedAt, setLastRefreshedAt] = React.useState<Date | null>(null);
  const [highlightedQualification, setHighlightedQualification] = React.useState<string | null>(null);
  const qualificationHeadingId = React.useId();

  const {
    data: resellerApi,
    isLoading: isResellerLoading,
    isFetching: isResellerFetching,
    isError: isResellerError,
    refetch: refetchReseller,
  } = useGetV4ManufacturerByRouteManufacturerIdResellerAndResellerIdQuery(
    { routeManufacturerId: applicationId, resellerId: resellerId ?? "", withSpecification: true },
    { skip: !applicationId || !resellerId }
  );

  const reseller = React.useMemo(() => {
    if (!resellerApi) return null;
    const converted = Reseller.convertFrom(resellerApi as ApiReseller);
    const qualificationForManufacturer = converted.qualifications?.find((q) => q.manufacturerId === applicationId)?.qualificationId;
    converted.isQualified = Boolean(
      qualificationForManufacturer && qualificationForManufacturer !== "Do Not Sell" && qualificationForManufacturer !== "Suspended"
    );
    return converted;
  }, [applicationId, resellerApi]);

  const isFetching = isResellerFetching;

  React.useEffect(() => {
    if (resellerApi && !isFetching) {
      setLastRefreshedAt(new Date());
    }
  }, [isFetching, resellerApi]);

  const ownQualification = React.useMemo(() => {
    if (!reseller) return undefined;
    return reseller.qualifications?.find(
      (q) => q.manufacturerId === applicationId && q.qualificationId !== "Do Not Sell" && q.qualificationId !== "Suspended"
    );
  }, [applicationId, reseller]);

  const otherQualifications = React.useMemo(() => {
    if (!reseller?.qualifications) return [];
    return reseller.qualifications.filter((q) => q.manufacturerId !== applicationId);
  }, [applicationId, reseller?.qualifications]);

  const highlightedQualificationCopy = React.useMemo(() => {
    if (!highlightedQualification) return null;
    const match = reseller?.qualifications?.find((q) => q.manufacturerId === highlightedQualification);
    if (!match) return null;
    return `${match.manufacturerPublicName || "Partner"} Ã¢â‚¬Â¢ ${match.qualificationId || "Qualification"}`;
  }, [highlightedQualification, reseller?.qualifications]);

  React.useEffect(() => {
    const previousTitle = document.title;
    if (reseller) {
      const resellerLabel = reseller.publicName || reseller.name || resellerId || "Reseller";
      document.title = `Reseller - ${resellerLabel} | ${previousTitle}`;
    }
    return () => {
      document.title = previousTitle;
    };
  }, [reseller, resellerId]);

  const pageContainerClassName = "space-y-8 pb-10";
  const surfaceClassName = "rounded-2xl border border-gray-100 bg-white p-6 shadow-sm";

  const handleRefresh = React.useCallback(async () => {
    await Promise.all([refetchReseller()]);
  }, [refetchReseller]);

  const assistantPrompt = React.useMemo(() => {
    if (!reseller || !resellerId) return null;
    const qualificationId = reseller.qualifications?.find((row) => row.manufacturerId === applicationId)?.qualificationId;
    const manufacturerName =
      reseller.qualifications?.find((q) => q.manufacturerId === applicationId)?.manufacturerPublicName || "Manufacturer";
    const resellerName = reseller.publicName || reseller.name || "Reseller";

    return buildResellerAssistantPrompt({
      applicationId,
      manufacturerName,
      resellerId,
      resellerName,
      isQualified: Boolean(reseller.isQualified),
      onHold: Boolean(reseller.onHold),
      qualificationId,
      lastInvoiceDateLabel: formatResellerDate(reseller.lastInvoiceDate),
      joinedDateLabel: formatResellerDate(reseller.creationDate, "Unknown"),
      activityCounts: {
        activeOrders: reseller.activeOrderDetails?.length ?? 0,
        completedOrders: reseller.completedOrderDetails?.length ?? 0,
        returns: reseller.returns?.length ?? 0,
        opportunities: reseller.opportunities?.length ?? 0,
      },
      salesSummary: {
        salesLast30Days: reseller.salesData?.salesLast30Days ?? 0,
        salesLast3Months: reseller.salesData?.salesLast3Months ?? 0,
        salesYearToDay: reseller.salesData?.salesYearToDay ?? 0,
      },
    });
  }, [applicationId, reseller, resellerId]);

  if (!resellerId) {
    return (
      <>
        <RmPageHeader
          title="Reseller"
          subtitle="Reseller profile"
          rightTopActions={(
            <DataRefreshAssistantCluster
              lastRefreshAt={lastRefreshedAt}
              isRefreshing={isFetching}
              disabled
            />
          )}
        />
        <RmBodyZone className={pageContainerClassName}>
          <div className={surfaceClassName}>
            <Alert variant="destructive" className="rounded-2xl border border-red-100 bg-white">
              <AlertTitle>Missing reseller</AlertTitle>
              <AlertDescription>
                This URL is missing a reseller ID. Return to{" "}
                <Link to={Path.resellers(applicationId)} className="font-semibold text-[color:var(--ops-accent)] hover:underline">
                  Resellers
                </Link>
                .
              </AlertDescription>
            </Alert>
          </div>
        </RmBodyZone>
      </>
    );
  }

  if (isResellerLoading && !reseller) {
    return (
      <>
        <RmPageHeader
          title="Reseller"
          subtitle="Loading reseller profile..."
          rightTopActions={(
            <DataRefreshAssistantCluster
              lastRefreshAt={lastRefreshedAt}
              isRefreshing={isFetching}
              onRefresh={handleRefresh}
              disabled
            />
          )}
        />
        <RmBodyZone className={pageContainerClassName} role="status" aria-live="polite">
          <div className={surfaceClassName}>
            <Skeleton className="h-8 w-1/2" />
            <Skeleton className="mt-3 h-5 w-1/3" />
            <div className="mt-6 grid gap-4 md:grid-cols-3">
              {Array.from({ length: 3 }).map((_, index) => (
                <Skeleton key={index} className="h-24 w-full rounded-2xl" />
              ))}
            </div>
          </div>
          <Skeleton className="h-[520px] w-full rounded-2xl" />
        </RmBodyZone>
      </>
    );
  }

  if (!reseller) {
    return (
      <>
        <RmPageHeader
          title="Reseller"
          subtitle="Reseller profile"
          rightTopActions={(
            <DataRefreshAssistantCluster
              lastRefreshAt={lastRefreshedAt}
              isRefreshing={isFetching}
              onRefresh={handleRefresh}
              disabled
            />
          )}
        />
        <RmBodyZone className={pageContainerClassName}>
          <div className={surfaceClassName}>
            <Alert variant="destructive" className="rounded-2xl border border-red-100 bg-white">
              <AlertTitle>Couldn't load reseller</AlertTitle>
              <AlertDescription className="space-y-2">
                <p>We ran into an issue loading this reseller profile. Please try again.</p>
                <Button type="button" variant="outline" onClick={() => refetchReseller()} className="rounded-xl">
                  Retry
                </Button>
              </AlertDescription>
            </Alert>
          </div>
        </RmBodyZone>
      </>
    );
  }

  const actionsMenu = (
    <Popover>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" size="sm" className="inline-flex items-center gap-2 rounded-xl">
          Actions
          <ChevronDown className="h-4 w-4" aria-hidden="true" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-auto rounded-xl border border-gray-100 bg-white p-0 shadow-sm">
        <ResellerActions reseller={reseller} />
      </PopoverContent>
    </Popover>
  );

  const manufacturerName = ownQualification?.manufacturerPublicName || "Manufacturer";
  const resellerName = reseller.publicName || reseller.name || "Reseller";
  const lastInvoiceDateLabel = formatResellerDate(reseller.lastInvoiceDate);
  const joinedDateLabel = formatResellerDate(reseller.creationDate, "Unknown");
  const hasRefreshError = isResellerError;
  const hasAnyActivity =
    (reseller.activeOrderDetails?.length ?? 0) +
    (reseller.completedOrderDetails?.length ?? 0) +
    (reseller.returns?.length ?? 0) +
    (reseller.opportunities?.length ?? 0) >
    0;

  return (
    <>
      <RmPageHeader
        title={resellerName}
        subtitle={`${manufacturerName} reseller profile`}
        rightTopActions={(
          <DataRefreshAssistantCluster
            lastRefreshAt={lastRefreshedAt}
            isRefreshing={isFetching}
            onRefresh={handleRefresh}
            assistantPromptOptions={assistantPrompt ? { prompt: assistantPrompt } : undefined}
            disabled={!assistantPrompt}
          />
        )}
        rightBottomActions={actionsMenu}
      />
      <RmBodyZone className="space-y-8 pb-10" aria-busy={isFetching}>
        <section className={surfaceClassName} aria-label="Reseller summary">
          <div className="grid gap-6">
            <div className="space-y-4 rounded-2xl border border-gray-100 bg-slate-50/60 p-5">
              <div className="flex items-start gap-4">
                {reseller.resellerId ? (
                  <img
                    src={`https://api.randmar.io/V4/Partner/${reseller.resellerId}/Account/Logo?width=144`}
                    alt={`${resellerName} logo`}
                    className="h-16 w-16 rounded-xl border border-gray-100 bg-white object-contain p-2"
                    loading="lazy"
                  />
                ) : (
                  <div className="h-16 w-16 rounded-xl border border-gray-100 bg-white" />
                )}
                <div className="min-w-0">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Reseller profile</p>
                  <h2 className="mt-1 text-[30px] font-semibold leading-tight text-slate-900">{resellerName}</h2>
                  <p className="mt-1 text-[15px] text-slate-600">
                    {[reseller.city, reseller.province, reseller.country].filter(Boolean).join(", ") || "Location unknown"}
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {reseller.isQualified ? (
                  <span className="inline-flex rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-emerald-700">
                    Qualified
                  </span>
                ) : null}
                <span className="inline-flex rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold uppercase tracking-wide text-slate-700">
                  {reseller.onHold ? "On hold" : "Active"}
                </span>
                {reseller.priority ? (
                  <span className="inline-flex rounded-full border border-sky-200 bg-sky-50 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-sky-700">
                    {reseller.priority}
                  </span>
                ) : null}
              </div>
            </div>
          </div>

          <div className="mt-6 grid gap-4 lg:grid-cols-2">
            <div className="space-y-4">
              <SalesSummary
                salesData={reseller?.salesData}
              />
              <div aria-label="Active orders stats">
                <h3 className="text-[18px] font-medium text-slate-900">Current open orders summary</h3>
                <p className="mt-1 text-[15px] text-slate-500">Current open purchase orders summary.</p>
                <div className="mt-4">
                  <OpenOrdersSummary openOrders={reseller.activeOrderDetails ?? []} />
                </div>
              </div>
            </div>
            <div className="rounded-2xl border border-gray-100 bg-white p-4">
              <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-500">Sales Statistics Overview</p>
              <SalesChart
                statistics={(reseller.salesStatistics ?? []) as ApiSalesStatistic[]}
                mode="month"
                chartHeight={280}
              />
            </div>
          </div>

          {hasRefreshError ? (
            <Alert variant="destructive" className="mt-6 rounded-2xl border border-red-100 bg-white">
              <AlertTitle>Some data couldn't be refreshed</AlertTitle>
              <AlertDescription className="space-y-2">
                <p>Parts of this page may be out of date. Try refreshing again.</p>
              </AlertDescription>
            </Alert>
          ) : null}
        </section>

        <section className="grid gap-6 lg:grid-cols-12" aria-labelledby={qualificationHeadingId}>
          <div className={["lg:col-span-7", surfaceClassName].join(" ")} id={sectionIds.qualifications}>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Partner fit</p>
            <h2 id={qualificationHeadingId} className="mt-2 text-[24px] font-semibold text-slate-900">
              Qualifications
            </h2>
            <p className="mt-1 text-[15px] text-slate-500">Qualification footprint across Randmar partners.</p>

            {(reseller.qualifications?.length ?? 0) > 0 ? (
              <div className="mt-6 space-y-6">
                {ownQualification && (
                  <div className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
                    <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-center">
                      <img
                        className="h-16 w-16 rounded-2xl border border-gray-100 object-contain p-2"
                        src={`https://api.randmar.io/V4/Partner/${applicationId}/Account/Logo?width=100`}
                        alt={`${manufacturerName} logo`}
                        loading="lazy"
                      />
                      <div>
                        <p className="text-[13px] text-slate-500">Your brand</p>
                        <p className="text-[20px] font-medium text-slate-900">{manufacturerName}</p>
                        <p className="mt-1 text-[15px] text-slate-500">
                          Qualification ID: <span className="font-semibold text-slate-900">{ownQualification.qualificationId}</span>
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {otherQualifications.length > 0 && (
                  <div>
                    <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Other partners</h3>
                    <ul className="mt-3 flex flex-wrap gap-3" role="list">
                      {otherQualifications.map((row) => (
                        <li key={row.manufacturerId || row.manufacturerPublicName || "partner"}>
                          <button
                            type="button"
                            onMouseEnter={() => setHighlightedQualification(row.manufacturerId || null)}
                            onFocus={() => setHighlightedQualification(row.manufacturerId || null)}
                            onMouseLeave={() => setHighlightedQualification(null)}
                            onBlur={() => setHighlightedQualification(null)}
                            className="group flex h-16 w-16 items-center justify-center rounded-2xl border border-gray-100 bg-white p-2 shadow-sm transition-all duration-200 hover:-translate-y-1 hover:border-[var(--ops-accent)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ops-focus-ring)]"
                            aria-pressed={highlightedQualification === row.manufacturerId}
                          >
                            <img
                              src={`https://api.randmar.io/V4/Partner/${row.manufacturerId}/Account/Logo?width=100`}
                              alt={`${row.manufacturerPublicName || "Manufacturer"} logo`}
                              className="h-full w-full object-contain"
                              loading="lazy"
                            />
                          </button>
                        </li>
                      ))}
                    </ul>
                    {highlightedQualificationCopy && (
                      <p className="mt-2 text-[13px] text-slate-500" role="status">
                        {highlightedQualificationCopy}
                      </p>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="mt-6 rounded-2xl border border-dashed border-gray-100 bg-gray-50/60 p-5">
                <p className="text-[15px] text-slate-500">No qualifications have been recorded for this reseller yet.</p>
              </div>
            )}
          </div>

          <aside className={["lg:col-span-5", surfaceClassName].join(" ")} aria-label="Reseller contact and key dates">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Reseller details</p>
            <h2 className="mt-2 text-[20px] font-medium text-slate-900">Contact information</h2>
            <p className="mt-1 text-[15px] text-slate-500">Primary contact and important account dates.</p>
            <div className="mt-6 grid gap-3">
              <div className="rounded-2xl border border-gray-100 bg-white p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Point of contact</p>
                <p className="mt-2 text-[15px] font-semibold text-slate-900">{reseller.contactName || "Not available"}</p>
              </div>
              <div className="rounded-2xl border border-gray-100 bg-white p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Email</p>
                <p className="mt-2 text-[15px] text-slate-900">{reseller.publicEmail || "Not available"}</p>
              </div>
              <div className="rounded-2xl border border-gray-100 bg-white p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Phone</p>
                <p className="mt-2 text-[15px] text-slate-900">{reseller.phone || "Not available"}</p>
              </div>
              <div className="rounded-2xl border border-gray-100 bg-white p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Key dates</p>
                <p className="mt-1 text-[13px] text-slate-500">
                  Last invoice (Randmar): <span className="font-semibold text-slate-900">{lastInvoiceDateLabel}</span>
                </p>
                <p className="mt-1 text-[13px] text-slate-500">
                  Joined Randmar: <span className="font-semibold text-slate-900">{joinedDateLabel}</span>
                </p>
              </div>
            </div>
          </aside>
        </section>

        <section className="space-y-6" aria-label="Orders and returns">
          {(reseller.activeOrderDetails || []).length > 0 && (
            <div className={surfaceClassName} id={sectionIds.activeOrders}>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Live fulfillment</p>
              <h2 className="mt-2 text-[20px] font-medium text-slate-900">Active orders</h2>
              <p className="mt-1 text-[15px] text-slate-500">Open purchase orders currently in motion.</p>
              <div className="mt-6 space-y-4">
                <OpenOrdersGrid productOrders={reseller.activeOrderDetails} defaultRowsPerPage={25} withHeader />
              </div>
            </div>
          )}

          {(reseller.completedOrderDetails || []).length > 0 && (
            <div className={surfaceClassName} id={sectionIds.completedOrders}>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Fulfilled</p>
              <h2 className="mt-2 text-[20px] font-medium text-slate-900">Completed orders</h2>
              <p className="mt-1 text-[15px] text-slate-500">Shipped and invoiced.</p>
              <div className="mt-6">
                <OpenOrdersGrid productOrders={reseller.completedOrderDetails} defaultRowsPerPage={10} withHeader />
              </div>
            </div>
          )}

          {reseller.returns && reseller.returns.length > 0 && (
            <div className={surfaceClassName} id={sectionIds.returns}>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Post-sale</p>
              <h2 className="mt-2 text-[20px] font-medium text-slate-900">Returns</h2>
              <p className="mt-1 text-[15px] text-slate-500">Defectives and claims.</p>
              <div className="mt-6">
                <ReturnGrid returns={reseller.returns} />
              </div>
            </div>
          )}

          {reseller.opportunities && reseller.opportunities.length > 0 && (
            <div className={surfaceClassName} id={sectionIds.opportunities}>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Bids</p>
              <h2 className="mt-2 text-[20px] font-medium text-slate-900">Opportunities</h2>
              <p className="mt-1 text-[15px] text-slate-500">Bids and rebates.</p>
              <div className="mt-6">
                <OpportunitiesGrid opportunities={reseller.opportunities} defaultRowsPerPage={25} />
              </div>
            </div>
          )}

          {!hasAnyActivity && (
            <div className={surfaceClassName}>
              <div className="mx-auto flex max-w-lg flex-col items-center gap-3 py-10 text-center">
                <Store className="h-10 w-10 text-slate-300" aria-hidden="true" />
                <p className="text-xl font-medium text-slate-900">No activity yet</p>
                <p className="text-[15px] text-slate-500">
                  Orders, returns, and opportunities will appear here as this reseller starts transacting.
                </p>
                <div className="mt-2">
                  <Button asChild size="sm" className="rounded-xl">
                    <Link to={Path.resellers(applicationId)}>Back to resellers</Link>
                  </Button>
                </div>
              </div>
            </div>
          )}
        </section>
      </RmBodyZone>
    </>
  );
};

export default withRoot(ResellerPage);
